import React, { useEffect, useMemo, useState } from 'react';
import { adminUsersAPI } from '../services/api';
import UserDocumentsModal from '../components/UserDocumentsModal';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    perPage: 20,
    totalItems: 0,
    hasPrevPage: false,
    hasNextPage: false,
  });
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const totalPages = useMemo(() => {
    if (!total || !limit) return 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [total, limit]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (email) params.email = email;
      if (name) params.name = name;
      const { data } = await adminUsersAPI.getUsers(params);
      const payload = data?.data || data;
      const usersList = payload?.users || [];
      const p = payload?.pagination || {};

      const totalItems = (payload?.total ?? payload?.totalUsers ?? p.total ?? p.totalUsers ?? p.totalItems ?? 0);
      const perPage = (p.usersPerPage ?? p.itemsPerPage ?? p.perPage ?? limit);
      const currentPage = (p.currentPage ?? page);
      const totalPages = (p.totalPages ?? Math.max(1, Math.ceil((totalItems || 0) / (perPage || 1))));
      const hasPrevPage = (typeof p.hasPrevPage === 'boolean') ? p.hasPrevPage : currentPage > 1;
      const hasNextPage = (typeof p.hasNextPage === 'boolean') ? p.hasNextPage : currentPage < totalPages;

      setUsers(usersList);
      setTotal(totalItems);
      setPagination({
        currentPage,
        totalPages,
        perPage,
        totalItems,
        hasPrevPage,
        hasNextPage,
      });

      // If backend adjusted page or perPage, sync local state without causing loops
      if (currentPage !== page) {
        setPage(currentPage);
      }
      if (perPage !== limit) {
        setLimit(perPage);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, limit]);

  const onApplyFilters = () => {
    setPage(1);
    fetchUsers();
  };

  const onClearFilters = () => {
    setEmail('');
    setName('');
    setPage(1);
    fetchUsers();
  };

  const effectivePage = pagination.currentPage || page;
  const effectivePerPage = pagination.perPage || limit;
  const effectiveTotal = (pagination.totalItems ?? total) || 0;
  const effectiveTotalPages = pagination.totalPages || Math.max(1, Math.ceil((effectiveTotal || 0) / (effectivePerPage || 1)));
  const showingFrom = users.length ? (effectivePage - 1) * effectivePerPage + 1 : 0;
  const showingTo = users.length ? Math.min(effectivePage * effectivePerPage, effectiveTotal) : 0;

  if (loading && users.length === 0) {
    return (
      <div className="d-flex justify-center align-center" style={{ minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.5rem' }}>Users</h1>
        <p style={{ color: 'var(--gray-600)' }}>
          View and manage all registered users.
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-body">
          <div className="grid grid-cols-4" style={{ gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Search Email</label>
              <input
                type="text"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Filter by email"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Search Name</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Filter by name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Per Page</label>
              <select
                className="form-input"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ alignSelf: 'end' }}>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <button onClick={onApplyFilters} disabled={loading} className="btn btn-primary">
                  Apply
                </button>
                <button onClick={onClearFilters} disabled={loading} className="btn btn-secondary">
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      {users.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{
                fontSize: '4rem',
                marginBottom: '1rem',
                opacity: 0.5,
              }}>
                👥
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-600)' }}>
                No users found
              </h3>
              <p style={{ margin: 0, color: 'var(--gray-500)' }}>
                Try adjusting your search criteria.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Phone</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id || u._id || idx}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{(page - 1) * limit + idx + 1}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{u.name || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{u.email || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{u.phone || '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => { setSelectedUser(u); setIsDocsOpen(true); }}
                        >
                          Documents
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {effectiveTotalPages > 1 && (
            <div className="card-footer">
              <div className="d-flex justify-between align-center">
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Showing {showingFrom} to {showingTo} of {total} users
                </div>
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={loading || (typeof pagination.hasPrevPage === 'boolean' ? !pagination.hasPrevPage : effectivePage <= 1)}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Previous
                  </button>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}>
                    Page {effectivePage} of {effectiveTotalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(effectiveTotalPages, p + 1))}
                    disabled={loading || (typeof pagination.hasNextPage === 'boolean' ? !pagination.hasNextPage : effectivePage >= effectiveTotalPages)}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <UserDocumentsModal
        isOpen={isDocsOpen}
        onClose={() => { setIsDocsOpen(false); setSelectedUser(null); }}
        user={selectedUser}
      />
    </div>
  );
};

export default AdminUsers;


